import React from 'react';
import { 
  Fuel, BusFront, TrendingUp, MapPin, Clock, AlertTriangle, 
  CheckCircle2, Info, Navigation, Search, Filter, MessageSquare, 
  ThumbsUp, ThumbsDown, Zap, Coins, Users, Plus, X, BarChart3, 
  ArrowUpRight, Download, Calendar
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, AreaChart, Area, Cell
} from 'recharts';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { formatCurrency as formatLocalCurrency } from '../utils/exchange-utils';

interface StationReport {
  id: string;
  name: string;
  location: string;
  price: number;
  status: 'Available' | 'Queue' | 'No Fuel';
  queueLength: 'Short' | 'Medium' | 'Long' | 'None';
  lastUpdated: string;
  reporter: string;
  reporterRank: string;
  verified: boolean;
  upvotes: number;
}

interface TransportFare {
  id: string;
  route: string;
  type: 'Danfo' | 'BRT' | 'Uber/Bolt' | 'Keke';
  price: number;
  normalPrice: number;
  status: 'Surging' | 'Normal' | 'Discount';
  lastUpdated: string;
  reporter: string;
}

interface BusStopStatus {
  id: string;
  name: string;
  crowdLevel: 'Low' | 'Moderate' | 'Heavy' | 'Extreme';
  waitTime: string;
  securityStatus: 'Safe' | 'Watchful' | 'Alert';
  lastUpdated: string;
}

const STATIONS: StationReport[] = [
  {
    id: 's1',
    name: 'NNPC Retail',
    location: 'Awolowo Rd, Ikoyi',
    price: 650,
    status: 'Available',
    queueLength: 'Short',
    lastUpdated: '5 mins ago',
    reporter: 'Tunde W.',
    reporterRank: 'Elder',
    verified: true,
    upvotes: 42
  },
  {
    id: 's2',
    name: 'TotalEnergies',
    location: 'Third Mainland Entrance',
    price: 670,
    status: 'Queue',
    queueLength: 'Long',
    lastUpdated: '12 mins ago',
    reporter: 'Nkechi O.',
    reporterRank: 'Guardian',
    verified: true,
    upvotes: 18
  },
  {
    id: 's3',
    name: 'Enyo Retail',
    location: 'Lekki Phase 1',
    price: 650,
    status: 'No Fuel',
    queueLength: 'None',
    lastUpdated: '1h ago',
    reporter: 'Segun',
    reporterRank: 'Neighbor',
    verified: false,
    upvotes: 5
  }
];

const FARES: TransportFare[] = [
  {
    id: 'f1',
    route: 'Oshodi to Obalende',
    type: 'Danfo',
    price: 1200,
    normalPrice: 800,
    status: 'Surging',
    lastUpdated: '10 mins ago',
    reporter: 'Bisi'
  },
  {
    id: 'f2',
    route: 'CMS to Ajah',
    type: 'BRT',
    price: 600,
    normalPrice: 600,
    status: 'Normal',
    lastUpdated: '30 mins ago',
    reporter: 'System'
  },
  {
    id: 'f3',
    route: 'Victoria Island to Ikeja',
    type: 'Uber/Bolt',
    price: 8500,
    normalPrice: 4500,
    status: 'Surging',
    lastUpdated: 'Just now',
    reporter: 'Chidi'
  }
];

const BUS_STOPS: BusStopStatus[] = [
  {
    id: 'bs1',
    name: 'Oshodi Interchange',
    crowdLevel: 'Extreme',
    waitTime: '45 mins',
    securityStatus: 'Safe',
    lastUpdated: '2 mins ago'
  },
  {
    id: 'bs2',
    name: 'Lekki Toll Gate (Palms)',
    crowdLevel: 'Moderate',
    waitTime: '15 mins',
    securityStatus: 'Watchful',
    lastUpdated: '10 mins ago'
  },
  {
    id: 'bs3',
    name: 'Berger Bus Stop',
    crowdLevel: 'Heavy',
    waitTime: '30 mins',
    securityStatus: 'Safe',
    lastUpdated: 'Just now'
  }
];

export function TransportFuelHub({ country = 'Nigeria' }: { country?: string }) {
  const [activeTab, setActiveTab] = React.useState<'fuel' | 'transport' | 'stops'>('fuel');
  const [search, setSearch] = React.useState('');
  
  const isNigeria = country === 'Nigeria';
  const currencyCode = country === 'UK' ? 'GBP' : country === 'USA' ? 'USD' : country === 'Ghana' ? 'GHS' : 'NGN';

  const STATIONS: StationReport[] = React.useMemo(() => {
    const nigeriaStations: StationReport[] = [
      { id: 's1', name: 'NNPC Retail', location: 'Awolowo Rd, Ikoyi', price: 650, status: 'Available', queueLength: 'Short', lastUpdated: '5 mins ago', reporter: 'Tunde W.', reporterRank: 'Elder', verified: true, upvotes: 42 },
      { id: 's2', name: 'TotalEnergies', location: 'Third Mainland Entrance', price: 670, status: 'Queue', queueLength: 'Long', lastUpdated: '12 mins ago', reporter: 'Nkechi O.', reporterRank: 'Guardian', verified: true, upvotes: 18 },
      { id: 's3', name: 'Enyo Retail', location: 'Lekki Phase 1', price: 650, status: 'No Fuel', queueLength: 'None', lastUpdated: '1h ago', reporter: 'Segun', reporterRank: 'Neighbor', verified: false, upvotes: 5 }
    ];
    const ukStations: StationReport[] = [
      { id: 'suk1', name: 'BP Connect', location: 'Oxford St, London', price: 1.45, status: 'Available', queueLength: 'None', lastUpdated: '10 mins ago', reporter: 'James T.', reporterRank: 'Admin', verified: true, upvotes: 120 },
      { id: 'suk2', name: 'Shell Select', location: 'Piccadilly Circus', price: 1.48, status: 'Queue', queueLength: 'Short', lastUpdated: '25 mins ago', reporter: 'Emma W.', reporterRank: 'Neighbor', verified: true, upvotes: 15 }
    ];
    const usaStations: StationReport[] = [
      { id: 'susa1', name: 'ExxonMobil', location: 'Downtown Houston, TX', price: 3.15, status: 'Available', queueLength: 'None', lastUpdated: '5 mins ago', reporter: 'John D.', reporterRank: 'Guardian', verified: true, upvotes: 85 },
      { id: 'susa2', name: 'Chevron', location: 'Santa Monica Blvd, LA', price: 4.25, status: 'Queue', queueLength: 'Medium', lastUpdated: '15 mins ago', reporter: 'Sarah P.', reporterRank: 'Neighbor', verified: true, upvotes: 32 }
    ];

    if (country === 'UK') return ukStations;
    if (country === 'USA') return usaStations;
    return nigeriaStations;
  }, [country]);

  const FARES: TransportFare[] = React.useMemo(() => {
    const nigeriaFares: TransportFare[] = [
      { id: 'f1', route: 'Oshodi to Obalende', type: 'Danfo', price: 1200, normalPrice: 800, status: 'Surging', lastUpdated: '10 mins ago', reporter: 'Bisi' },
      { id: 'f2', route: 'CMS to Ajah', type: 'BRT', price: 600, normalPrice: 600, status: 'Normal', lastUpdated: '30 mins ago', reporter: 'System' },
      { id: 'f3', route: 'VI to Ikeja', type: 'Uber/Bolt', price: 8500, normalPrice: 4500, status: 'Surging', lastUpdated: 'Just now', reporter: 'Chidi' }
    ];
    const ukFares: TransportFare[] = [
      { id: 'fuk1', route: 'Paddington to Waterloo', type: 'Tube', price: 6.70, normalPrice: 6.70, status: 'Normal', lastUpdated: '5 mins ago', reporter: 'James T.' },
      { id: 'fuk2', route: 'Heathrow Express', type: 'Train', price: 25.00, normalPrice: 22.00, status: 'Surging', lastUpdated: '20 mins ago', reporter: 'System' }
    ];
    if (country === 'UK') return ukFares;
    return nigeriaFares;
  }, [country]);

  const BUS_STOPS: BusStopStatus[] = React.useMemo(() => {
    const nigeriaStops: BusStopStatus[] = [
      { id: 'bs1', name: 'Oshodi Interchange', crowdLevel: 'Extreme', waitTime: '45 mins', securityStatus: 'Safe', lastUpdated: '2 mins ago' },
      { id: 'bs2', name: 'Lekki Toll Gate (Palms)', crowdLevel: 'Moderate', waitTime: '15 mins', securityStatus: 'Watchful', lastUpdated: '10 mins ago' }
    ];
    const ukStops: BusStopStatus[] = [
      { id: 'bsuk1', name: 'King\'s Cross', crowdLevel: 'Heavy', waitTime: '10 mins', securityStatus: 'Safe', lastUpdated: 'Just now' },
      { id: 'bsuk2', name: 'Victoria Station', crowdLevel: 'Moderate', waitTime: '5 mins', securityStatus: 'Safe', lastUpdated: '8 mins ago' }
    ];
    if (country === 'UK') return ukStops;
    return nigeriaStops;
  }, [country]);
  const [isReporting, setIsReporting] = React.useState(false);
  const [showAnalytics, setShowAnalytics] = React.useState(false);
  const [reportForm, setReportForm] = React.useState({
    name: '',
    price: '',
    status: 'Available' as 'Available' | 'Queue' | 'No Fuel',
    queueLength: 'None' as 'None' | 'Short' | 'Medium' | 'Long'
  });

  const handleSubmitReport = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success('Report Submitted', {
      description: `Your update for ${reportForm.name} has been sent for verification. You earned 10 Sentlo Coins!`,
      icon: <Coins className="w-4 h-4 text-amber-500" />
    });
    setIsReporting(false);
    setReportForm({ name: '', price: '', status: 'Available', queueLength: 'None' });
  };

  const filteredStations = STATIONS.filter(s => 
    s.name.toLowerCase().includes(search.toLowerCase()) || 
    s.location.toLowerCase().includes(search.toLowerCase())
  );

  const filteredFares = FARES.filter(f => 
    f.route.toLowerCase().includes(search.toLowerCase())
  );

  const filteredStops = BUS_STOPS.filter(s => 
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
      <div className="flex flex-col gap-2">
        <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900">
          <Navigation className="w-5 h-5 text-green-600" />
          Transport & Fuel Hub
        </h2>
        <p className="text-sm text-gray-500">Real-time crowdsourced utility for your daily commute.</p>
      </div>

      {/* Tabs */}
      <div className="flex bg-gray-50 p-1 rounded-xl overflow-x-auto no-scrollbar">
        <button
          onClick={() => setActiveTab('fuel')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-[11px] font-black uppercase tracking-tighter rounded-lg transition-all whitespace-nowrap ${
            activeTab === 'fuel' 
              ? 'bg-white text-green-700 shadow-sm' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Fuel className="w-3.5 h-3.5" />
          Fuel
        </button>
        <button
          onClick={() => setActiveTab('transport')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-[11px] font-black uppercase tracking-tighter rounded-lg transition-all whitespace-nowrap ${
            activeTab === 'transport' 
              ? 'bg-white text-green-700 shadow-sm' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <BusFront className="w-3.5 h-3.5" />
          Fares
        </button>
        <button
          onClick={() => setActiveTab('stops')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-[11px] font-black uppercase tracking-tighter rounded-lg transition-all whitespace-nowrap ${
            activeTab === 'stops' 
              ? 'bg-white text-green-700 shadow-sm' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          Stops
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input 
          type="text"
          placeholder={
            activeTab === 'fuel' ? "Search stations..." : 
            activeTab === 'transport' ? "Search routes..." : 
            "Search bus stops..."
          }
          className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-green-500 transition-all"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4">
        <AnimatePresence mode="wait">
          {activeTab === 'fuel' && (
            <motion.div 
              key="fuel"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex flex-col gap-4"
            >
              {filteredStations.map((station) => (
                <div key={station.id} className="p-4 rounded-xl border border-gray-100 bg-white hover:border-green-100 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        {station.name}
                        {station.verified && <CheckCircle2 className="w-4 h-4 text-blue-500" />}
                      </h3>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {station.location}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-700">{formatLocalCurrency(station.price, currencyCode)}</p>
                      <p className="text-[10px] text-gray-400">{isNigeria ? 'per litre' : 'per unit'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      station.status === 'Available' ? 'bg-green-50 text-green-700' :
                      station.status === 'Queue' ? 'bg-amber-50 text-amber-700' :
                      'bg-red-50 text-red-700'
                    }`}>
                      {station.status}
                    </span>
                    {station.status === 'Queue' && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {station.queueLength} queue
                      </span>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                      <button className="p-1.5 hover:bg-gray-50 rounded-lg text-gray-400 hover:text-green-600 transition-colors">
                        <ThumbsUp className="w-4 h-4" />
                      </button>
                      <span className="text-xs font-medium text-gray-600">{station.upvotes}</span>
                    </div>
                  </div>
                </div>
              ))}

              <AnimatePresence>
                {isReporting && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <form 
                      onSubmit={handleSubmitReport}
                      className="p-5 rounded-2xl bg-zinc-50 border border-zinc-200 space-y-4 mb-2"
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Report Station Status</h4>
                        <button type="button" onClick={() => setIsReporting(false)}><X className="w-4 h-4 text-zinc-400" /></button>
                      </div>

                      <div className="space-y-3">
                        <div className="space-y-1">
                          <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest ml-1">Station Name</label>
                          <input 
                            required
                            type="text"
                            placeholder="e.g. NNPC Retail, TotalEnergies..."
                            value={reportForm.name}
                            onChange={(e) => setReportForm({ ...reportForm, name: e.target.value })}
                            className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-2.5 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-green-500/10"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest ml-1">Price (₦/Litre)</label>
                            <input 
                              required
                              type="number"
                              placeholder="650"
                              value={reportForm.price}
                              onChange={(e) => setReportForm({ ...reportForm, price: e.target.value })}
                              className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-2.5 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-green-500/10"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest ml-1">Fuel Status</label>
                            <select 
                              value={reportForm.status}
                              onChange={(e) => setReportForm({ ...reportForm, status: e.target.value as any })}
                              className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-2.5 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-green-500/10 appearance-none"
                            >
                              <option value="Available">Available</option>
                              <option value="Queue">Queue</option>
                              <option value="No Fuel">No Fuel</option>
                            </select>
                          </div>
                        </div>

                        {reportForm.status === 'Queue' && (
                          <div className="space-y-1">
                            <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest ml-1">Queue Length</label>
                            <div className="flex gap-2">
                              {['Short', 'Medium', 'Long'].map((l) => (
                                <button
                                  key={l}
                                  type="button"
                                  onClick={() => setReportForm({ ...reportForm, queueLength: l as any })}
                                  className={`flex-1 py-2 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all border ${reportForm.queueLength === l ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-400 border-zinc-200'}`}
                                >
                                  {l}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <button 
                        type="submit"
                        className="w-full bg-green-600 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-[0.1em] flex items-center justify-center gap-2 hover:bg-green-700 transition-all active:scale-[0.98]"
                      >
                        Submit Station Update
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>

              {!isReporting && (
                <div className="space-y-2 mt-2">
                  <button 
                    onClick={() => setIsReporting(true)}
                    className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-green-500 hover:text-green-600 transition-all flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Report Station Status
                  </button>
                  <p className="text-[8px] font-black text-center text-zinc-400 uppercase tracking-widest">
                    Community Updates are FREE • No NIN Required
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'transport' && (
            <motion.div 
              key="transport"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex flex-col gap-4"
            >
              <div className="bg-amber-50 p-3 rounded-xl border border-amber-100 mb-2">
                <div className="flex gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>Rain Alert:</strong> Fares are surging across Lagos due to heavy downpour. Expect 50-100% increases.
                  </p>
                </div>
              </div>

              {filteredFares.map((fare) => (
                <div key={fare.id} className="p-4 rounded-xl border border-gray-100 bg-white hover:border-green-100 transition-colors shadow-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-black uppercase tracking-wider text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                          {fare.type}
                        </span>
                        {fare.status === 'Surging' && (
                          <span className="flex items-center gap-0.5 text-[10px] font-black text-red-600 bg-red-50 px-1.5 py-0.5 rounded italic">
                            <TrendingUp className="w-3 h-3" />
                            SURGE
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900">{fare.route}</h3>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-black italic ${fare.status === 'Surging' ? 'text-red-600' : 'text-green-700'}`}>
                        {currency}{fare.price.toLocaleString()}
                      </p>
                      <p className="text-[10px] text-gray-400">Normal: {currency}{fare.normalPrice.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {activeTab === 'stops' && (
            <motion.div 
              key="stops"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex flex-col gap-4"
            >
              {filteredStops.map((stop) => (
                <div key={stop.id} className="p-4 rounded-xl border border-gray-100 bg-white hover:border-green-100 transition-colors shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{stop.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded-md ${
                          stop.securityStatus === 'Safe' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {stop.securityStatus}
                        </span>
                        <span className="text-[10px] text-gray-400">{stop.lastUpdated}</span>
                      </div>
                    </div>
                    <div className="text-right">
                       <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-full ${
                        stop.crowdLevel === 'Extreme' ? 'bg-red-100 text-red-700' :
                        stop.crowdLevel === 'Heavy' ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {stop.crowdLevel}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-xl border border-zinc-100">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-zinc-400" />
                      <span className="text-xs text-zinc-600 font-bold uppercase tracking-tighter">Est. Wait</span>
                    </div>
                    <span className="text-sm font-black text-zinc-900 italic">{stop.waitTime}</span>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="bg-zinc-900 p-4 rounded-xl text-white">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-300" />
            <span className="text-sm font-bold tracking-tight uppercase italic">Sentlo Premium</span>
          </div>
          <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded">Partner Hub</span>
        </div>
        <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
          Official transit data provided by <strong className="text-white">LASG</strong> and <strong className="text-white">LAMATA</strong>.
        </p>
        <button 
          onClick={() => setShowAnalytics(true)}
          className="w-full py-2 bg-white text-zinc-900 text-xs font-bold rounded-lg hover:bg-zinc-100 transition-colors uppercase italic"
        >
          View Official Analytics
        </button>
      </div>

      <AnimatePresence>
        {showAnalytics && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
            onClick={() => setShowAnalytics(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-2xl rounded-[32px] overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-zinc-100 flex items-center justify-between bg-zinc-900 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center border border-green-500/30 text-green-400">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-widest italic">Official Agency Analytics</h3>
                    <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-tighter">B2B Dashboard • Premium Access</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowAnalytics(false)}
                  className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: 'Avg Fuel Price', value: '₦656.40', trend: '+1.2%', up: true },
                    { label: 'Transit Load', value: '84%', trend: '-4.5%', up: false },
                    { label: 'Active Pumps', value: '1,240', trend: 'Stable', up: null },
                    { label: 'Sync Status', value: 'Live', trend: '99.9%', up: true },
                  ].map((stat, i) => (
                    <div key={i} className="bg-zinc-50 rounded-2xl p-4 border border-zinc-100">
                      <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">{stat.label}</p>
                      <p className="text-sm font-black text-zinc-900 uppercase italic mb-1">{stat.value}</p>
                      {stat.up !== null && (
                        <div className={`flex items-center gap-1 text-[8px] font-black uppercase ${stat.up ? 'text-green-600' : 'text-red-600'}`}>
                          {stat.up ? <ArrowUpRight className="w-2.5 h-2.5" /> : <TrendingUp className="rotate-90 w-2.5 h-2.5" />}
                          {stat.trend}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Main Chart */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between px-1">
                    <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                      <TrendingUp className="w-3.5 h-3.5" />
                      Weekly Fuel Availability Index
                    </h4>
                    <div className="flex gap-2">
                      <button className="text-[8px] font-black uppercase bg-zinc-100 px-2 py-1 rounded text-zinc-500">7D</button>
                      <button className="text-[8px] font-black uppercase bg-zinc-900 px-2 py-1 rounded text-white text-white">30D</button>
                    </div>
                  </div>
                  <div className="h-[200px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={[
                        { name: 'Mon', val: 65 }, { name: 'Tue', val: 78 },
                        { name: 'Wed', val: 72 }, { name: 'Thu', val: 85 },
                        { name: 'Fri', val: 92 }, { name: 'Sat', val: 88 },
                        { name: 'Sun', val: 95 }
                      ]}>
                        <defs>
                          <linearGradient id="colorVal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#16a34a" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f4f4f5" />
                        <XAxis dataKey="name" fontSize={10} fontWeight={900} tick={{fill: '#a1a1aa'}} axisLine={false} tickLine={false} />
                        <YAxis hide />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#18181b', 
                            border: 'none', 
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}
                        />
                        <Area type="monotone" dataKey="val" stroke="#16a34a" strokeWidth={3} fillOpacity={1} fill="url(#colorVal)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Secondary Data */}
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Top Demand Hubs</h4>
                    <div className="space-y-3">
                      {[
                        { name: 'Lekki Phase 1', load: 92, color: '#16a34a' },
                        { name: 'Ikeja Alausa', load: 85, color: '#16a34a' },
                        { name: 'Victoria Island', load: 78, color: '#eab308' },
                        { name: 'Surulere', load: 45, color: '#ef4444' }
                      ].map((hub, i) => (
                        <div key={i} className="space-y-1.5">
                          <div className="flex justify-between items-center text-[10px] font-black uppercase italic">
                            <span className="text-zinc-600">{hub.name}</span>
                            <span className="text-zinc-900">{hub.load}% Load</span>
                          </div>
                          <div className="h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${hub.load}%` }}
                              transition={{ duration: 1, delay: 0.2 + i * 0.1 }}
                              className="h-full rounded-full"
                              style={{ backgroundColor: hub.color }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-zinc-900 rounded-[24px] p-5 text-white flex flex-col justify-between overflow-hidden relative group">
                    <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                      <Download className="w-24 h-24" />
                    </div>
                    <div>
                      <h5 className="text-[11px] font-black uppercase tracking-widest mb-1 italic">Monthly Summary</h5>
                      <p className="text-[9px] text-zinc-400 uppercase font-bold">Generated Feb 17, 2026</p>
                    </div>
                    <div className="mt-6 space-y-3">
                      <p className="text-[10px] text-zinc-300 leading-relaxed font-medium italic">
                        "Supply stability has improved by 14% across the Lagos Mainland sector following the NiMet maintenance window."
                      </p>
                      <button className="w-full bg-white text-zinc-900 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-zinc-100 transition-colors">
                        <Download className="w-3.5 h-3.5" />
                        Download PDF Report
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-zinc-50 border-t border-zinc-100 flex flex-col sm:flex-row items-center gap-4">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-7 h-7 rounded-full border-2 border-white bg-zinc-200" />
                  ))}
                </div>
                <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-tight text-center sm:text-left">
                  Joined by <strong className="text-zinc-900">12 verified official partners</strong> viewing this data live.
                </p>
                <button 
                  onClick={() => setShowAnalytics(false)}
                  className="sm:ml-auto w-full sm:w-auto px-8 py-3 bg-zinc-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.1em] hover:bg-black transition-all active:scale-[0.98]"
                >
                  Close Terminal
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
